import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { NovovilleButton, Typography } from '@/components/novoville';
import NovovilleRating from '@/components/novoville/NovovilleRating';
import { toast } from '@/components/ui/use-toast';
import { translate } from '../../services/TranslationService';
import { postRateReport } from './actions';

const ReportRating = ({ report }) => {
  const dispatch = useDispatch();
  const isRateReportButtonLoading = useSelector(
    (state) => state.reportsReducer.isRateReportButtonLoading
  );
  const [selectedRating, setSelectedRating] = useState(0);

  const isUsersOwnReport = report?.is_mine !== false;
  const canUserRateReport = !!report?.user_can_rate;
  const hasUserRatedReport = Number(report?.rate_actions_user_count) === 1;
  const userRate = report?.user_rate;
  const shouldShowAlreadyRated = hasUserRatedReport && isUsersOwnReport;
  const shouldShowRateForm = canUserRateReport && isUsersOwnReport;

  if (!shouldShowAlreadyRated && !shouldShowRateForm) {
    return null;
  }

  const submitRating = () => {
    if (!selectedRating) return;

    dispatch(postRateReport(report?.authority_name, report?.id, selectedRating))
      .then(() => {
        setSelectedRating(0);
        toast({
          title: translate('success_exclamation_mark'),
          description: translate(
            'Your rating has been submitted. Thank you for your feedback!'
          ),
          variant: 'success',
        });
      })
      .catch(() => {
        toast({
          title: translate('OTHER.errorOcurred'),
          description: translate('Failed to rate report. Try again later.'),
          variant: 'destructive',
        });
      });
  };

  return (
    <div className="flex flex-col gap-[16px]">
      {shouldShowAlreadyRated && (
        <div className="flex items-center gap-[12px] rounded-lg bg-gray-50 p-[16px]">
          <Info className="h-5 w-5 shrink-0 text-blueLink" />
          <Typography variant="regular_14" className="text-gray-700">
            {`${translate('You have already rated this report')} ${translate('with')} ${userRate} ${translate('out of')} 5 ${translate('stars')}.`}
          </Typography>
        </div>
      )}
      {shouldShowRateForm && (
        <div className="flex flex-col gap-[16px]">
          <div className="flex flex-col gap-[4px]">
            <Typography variant="semibold_16" className="text-gray-800">
              {translate('Help us improve!')}
            </Typography>
            <Typography variant="regular_14" className="text-gray-600">
              {translate(
                'Rate your experience with how this report was resolved.'
              )}
            </Typography>
          </div>
          <div className="flex flex-col items-start gap-[16px]">
            <NovovilleRating
              rating={selectedRating}
              onChange={setSelectedRating}
              disabled={isRateReportButtonLoading}
            />
            <NovovilleButton
              className="md:max-w-20 w-full"
              variant="primary"
              disabled={!selectedRating}
              loading={isRateReportButtonLoading}
              onClick={submitRating}
            >
              {translate('SUBMIT')}
            </NovovilleButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportRating;
